import { motion } from "framer-motion";
import { Layers, GraduationCap, Flag, Check } from "lucide-react";

const CATS = [
  {
    icon: Layers,
    tag: "Category 01",
    title: "Digital products",
    desc: 'The bundles, templates and "tools" sold through slick ads that fall apart on download.',
    items: [
      "Templates, planners & spreadsheets",
      "Canva & social media packs",
      'Ebooks & "mastery" guides',
      "AI prompt packs",
      "Resell-rights (MRR) & done-for-you bundles",
      "BOQ, estimation & site-doc bundles",
      "Presets & LUTs",
    ],
  },
  {
    icon: GraduationCap,
    tag: "Category 02",
    title: "Courses & webinars",
    desc: 'The ₹9-to-₹40,000 ladders, the fake-scarcity seminars, the "next class" that never delivers.',
    items: [
      "Instagram & social growth",
      '"Make money online" courses',
      "AI-skills courses",
      "Trading, stock & crypto",
      "Exam & coaching (edtech)",
      "Freelancing, dropshipping & e-commerce",
      'High-ticket "mentorship"',
    ],
  },
];

export const Categories = () => (
  <section data-testid="categories-section" className="py-24 sm:py-32 bg-white border-y border-black/5">
    <div className="max-w-6xl mx-auto px-6">
      <motion.div
        initial={{ opacity: 0, y: 24 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-80px" }}
        transition={{ duration: 0.6 }}
        className="max-w-2xl"
      >
        <div className="flex items-center gap-4">
          <span className="font-display text-4xl sm:text-5xl font-bold outline-word leading-none select-none" aria-hidden="true">06</span>
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-trust">Two shelves, one shared truth</p>
        </div>
        <h2 className="mt-4 font-display text-4xl sm:text-5xl font-bold tracking-tight leading-tight">
          The products India buys most — and gets burned on most.
        </h2>
        <p className="mt-4 text-base sm:text-lg text-gray-600">
          These aren't guesses. They're the highest-volume digital products and courses in the market right now —
          exactly where the complaints pile up.
        </p>
      </motion.div>

      <div className="mt-14 grid md:grid-cols-2 gap-6">
        {CATS.map((c, i) => (
          <motion.article
            key={c.title}
            data-testid={`category-card-${i}`}
            className="rounded-2xl bg-paper border border-black/5 p-8 sm:p-10 shadow-[0_10px_36px_-14px_rgba(17,24,39,0.12)] transition-[transform,box-shadow] duration-300 hover:-translate-y-1 hover:shadow-lg"
            initial={{ opacity: 0, y: 32 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.55, delay: i * 0.12 }}
          >
            <div className="flex items-center justify-between">
              <span className="inline-flex w-11 h-11 items-center justify-center rounded-xl bg-trust-soft text-trust">
                <c.icon size={20} />
              </span>
              <span className="text-xs font-bold uppercase tracking-[0.2em] text-gray-400">{c.tag}</span>
            </div>
            <h3 className="mt-6 font-display text-2xl font-semibold tracking-tight">{c.title}</h3>
            <p className="mt-2 text-sm text-gray-600">{c.desc}</p>
            <ul className="mt-6 space-y-2.5">
              {c.items.map((item) => (
                <li key={item} className="flex items-start gap-2.5 text-sm text-gray-700">
                  <Check size={15} className="mt-0.5 shrink-0 text-verified" />
                  {item}
                </li>
              ))}
            </ul>
          </motion.article>
        ))}
      </div>

      <motion.div
        data-testid="resell-rights-callout"
        className="mt-8 rounded-2xl border border-gold/50 bg-gold-soft p-8 sm:p-10 flex flex-col sm:flex-row gap-6"
        initial={{ opacity: 0, y: 32 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-60px" }}
        transition={{ duration: 0.6 }}
      >
        <span className="inline-flex shrink-0 w-12 h-12 items-center justify-center rounded-xl bg-gold/25 text-yellow-700">
          <Flag size={22} />
        </span>
        <div>
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-yellow-700">The resell-rights flag</p>
          <h3 className="mt-2 font-display text-xl sm:text-2xl font-semibold tracking-tight">
            The one thing no other review site checks
          </h3>
          <p className="mt-3 text-sm sm:text-base text-gray-700 leading-relaxed max-w-3xl">
            Most junk bundles are the same free files resold again and again under "master resell rights." Every
            digital-product listing here answers one question up front:{" "}
            <b>is this a resell-rights pack?</b> If the main value of a product is selling it onward, you deserve to
            know before you pay.
          </p>
        </div>
      </motion.div>
    </div>
  </section>
);
